import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client, { REFRESH_KEY, TOKEN_KEY, getAccessToken } from "../api/client";
import { trackActiveUser } from "../utils/metaPixel";

/**
 * Holds the signed-in user, their role, and the menu the backend says they
 * may see.
 *
 * Before this, the app had no idea who was logged in: it stored the phone
 * number under a key called "name", the profile dropdown rendered a
 * hardcoded name and email, and the sidebar was a fixed list in JSX with no
 * concept of roles at all.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(null);
    setRole(null);
    setMenu([]);
  }, []);

  const loadProfile = useCallback(async () => {
    if (!getAccessToken()) {
      clearSession();
      setLoading(false);
      return;
    }

    try {
      const { data } = await client.get("/api/me");
      setUser(data.user);
      setRole(data.role);
      setMenu(data.menu || []);
      if (data.role !== "Admin") trackActiveUser();
    } catch (error) {
      console.error("Could not load profile:", error);
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // The axios interceptor fires this when a refresh fails for good.
  useEffect(() => {
    const onExpired = () => clearSession();
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [clearSession]);

  const login = useCallback(
    async (accessToken, refreshToken) => {
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_KEY, refreshToken);
      setLoading(true);
      await loadProfile();
    },
    [loadProfile]
  );

  const logout = useCallback(async () => {
    try {
      // Without the refresh token the server cannot revoke it, and it would
      // stay usable for up to seven days after logging out.
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) await client.post("/api/logout", { refresh_token: refreshToken });
    } catch (error) {
      // A failed logout call should never trap the user in the app.
      console.warn("Logout call failed, clearing session anyway:", error);
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      role,
      menu,
      loading,
      isAuthenticated: Boolean(user),
      isAdmin: role === "Admin",
      login,
      logout,
      reload: loadProfile,
    }),
    [user, role, menu, loading, login, logout, loadProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}
